import type { PostBotConfigInput, PostBotMutation, PostBotPipeline } from '../../lib/api-client.js';
export const pipelineReasons:Record<string,string>={
  cloudflare_backoff:'Maloum’s Cloudflare protection temporarily blocked loading. The bot will wait 15 minutes before trying again.',
  folder_catalog_unavailable:'Maloum’s folder list could not be loaded.',
  categories_unavailable:'Maloum’s post categories could not be loaded.',
  folder_media_unavailable:'Media in a selected folder could not be loaded from Maloum.',
  folder_missing:'A selected folder is missing. Choose an available folder and save settings.',
  folder_empty:'A selected folder has no usable pictures or videos. Add media or choose another folder.',
  no_enabled_lanes:'Enable a posting type and choose its folder.',
  capacity_limited:'Some posts could not fit the selected schedule. Check the time window.',
  pipeline_step_failed:'A scheduling step failed unexpectedly.',
};
/** "refreshing…" only while a refresh is queued or running. Older API builds kept the flag set across a
 *  blocked or retrying pipeline, so the pipeline stage wins whenever the config query carries one. */
export function catalogRefreshing(flag:boolean|undefined,pipeline:Pick<PostBotPipeline,'stage'>|null|undefined):boolean {
  if(!flag)return false;
  return !pipeline||['queued','loading_media','planning'].includes(pipeline.stage);
}
/** One line beside the folder status, so a stopped or waiting pipeline is visible without scrolling. */
export function pipelineNotice(pipeline:PostBotPipeline|null|undefined,missingLanes:string[]=[]):{tone:'danger'|'muted';text:string}|null {
  if(!pipeline)return null;
  const reason=pipelineReasons[pipeline.error??'']??pipelineReasons.pipeline_step_failed;
  if(pipeline.stage==='blocked'){
    const where=pipeline.error==='folder_missing'&&missingLanes.length?` Affected: ${missingLanes.join(', ')}.`:'';
    return {tone:'danger',text:`Scheduling stopped, not refreshing. ${reason}${where}`};
  }
  if(pipeline.stage==='retrying'){
    const at=pipeline.nextRetryAt?` Next retry ${new Date(pipeline.nextRetryAt).toLocaleTimeString()}.`:'';
    return {tone:'muted',text:`Waiting to retry automatically. ${reason}${at}`};
  }
  return null;
}
/** Whitelist mutable fields: never PUT status, seed, timestamps or capability metadata. */
export function mutableConfig(config:PostBotConfigInput):PostBotConfigInput {
  return {selectedCategoryIds:[...(config.selectedCategoryIds??[])].sort(),captionCase:config.captionCase??'lowercase',paused:config.paused,timezone:config.timezone,handoffLeadHours:config.handoffLeadHours,
    lanes:config.lanes.map(l=>({lane:l.lane,enabled:l.enabled,folderId:l.folderId,recurrenceFreq:l.recurrenceFreq,postsPerDay:l.recurrenceFreq==='weekly'?1:l.postsPerDay,recurrenceDays:l.recurrenceFreq==='daily'?[]:[...l.recurrenceDays].sort(),windowStartMin:l.windowStartMin,windowEndMin:l.windowEndMin})).sort((a,b)=>a.lane.localeCompare(b.lane))};
}
export const configDirty = (draft:PostBotConfigInput,saved:PostBotConfigInput) => JSON.stringify(mutableConfig(draft))!==JSON.stringify(mutableConfig(saved));
export function mutationMessage(result:PostBotMutation,action:string):string {
  const prefix=result.changed?`${action} saved.`:`${action} complete.`;
  if(result.refreshQueued)return `Request queued. Folder media will load before scheduling. Follow the progress below.`;
  if(result.planningQueued)return `Request queued. Folder media will load before scheduling. Follow the progress below.`;
  if(result.disabledReason==='post_bot_disabled')return `${prefix} Post bot is disabled; no refresh or scheduling queued.`;
  if(result.disabledReason)return `${prefix} No work queued: ${result.disabledReason.replaceAll('_',' ')}.`;
  return result.changed?prefix:'No changes.';
}
export function thumbnailPath(accountId:string,enabled:boolean,path:string|null):string|null {
  if(!enabled||!path)return null;
  const prefix=`/api/post-bot/accounts/${encodeURIComponent(accountId)}/media/`;
  return path.startsWith(prefix)&&/^[-a-zA-Z0-9_%]+\/thumb$/.test(path.slice(prefix.length))?path:null;
}
export function relativeTime(value:string|null):string {
  if(!value)return 'No time scheduled'; const minutes=Math.round((Date.parse(value)-Date.now())/60000);
  if(!Number.isFinite(minutes))return '';
  return new Intl.RelativeTimeFormat('en',{numeric:'auto'}).format(Math.round(minutes/(Math.abs(minutes)>=1440?1440:Math.abs(minutes)>=60?60:1)),Math.abs(minutes)>=1440?'day':Math.abs(minutes)>=60?'hour':'minute');
}

/** Use persisted activation/pause state, never unsaved form values. */
export function schedulingControls(config:{activated:boolean;paused:boolean;systemEnabled:boolean},pending:boolean,dirty:boolean) {
  const running=config.activated&&!config.paused;
  return {startLabel:config.activated?'Resume scheduling':'Start scheduling',startDisabled:pending||dirty||!config.systemEnabled||running,pauseDisabled:pending||dirty||!running};
}
